// File: frontend/src/views/private/admin/AI.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain } from 'lucide-react';
import AdminPageTemplate from '../../../components/layout/AdminPageTemplate';
import { AgentStatsCards, AgentTable, AgentDetailsModal } from '../../../components/ai';
import { useAuth } from '../../../hooks/useAuth';
import apiService from '../../../services/apiService';
import styles from './AI.module.css';

const AI = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingAgents, setIsLoadingAgents] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState(null);

  // Load agents and stats on mount
  useEffect(() => {
    loadAgents();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAgents = async () => {
    setIsLoadingAgents(true);
    setError(null);

    try {
      console.log('📥 Loading agents...');

      const response = await apiService.getAgents({}, user?.user_id);

      console.log('✅ Agents loaded:', response);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load agents');
      }

      setAgents(response.data.agents || []);
    } catch (err) {
      console.error('❌ Load agents error:', err);
      setError(err.message || 'Failed to load agents');
    } finally {
      setIsLoadingAgents(false);
    }
  };

  const loadStats = async () => {
    setIsLoadingStats(true);

    try {
      console.log('📊 Loading agent stats...');

      const response = await apiService.getAgentStats({}, user?.user_id);

      console.log('✅ Stats loaded:', response);

      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('❌ Load stats error:', err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleViewDetails = async (agent) => {
    setIsModalOpen(true);
    setIsLoadingDetails(true);
    setSelectedAgent(null);

    try {
      console.log('📥 Loading agent details:', agent.agent_name, agent.version);

      const response = await apiService.getAgentDetails(
        {
          agent_name: agent.agent_name,
          version: agent.version
        },
        user?.user_id
      );

      console.log('✅ Agent details loaded:', response);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load agent details');
      }

      setSelectedAgent(response.data);
    } catch (err) {
      console.error('❌ Load agent details error:', err);
      setError(err.message || 'Failed to load agent details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAgent(null);
  };

  const handleStartChat = async (agent) => {
    try {
      console.log('🚀 Starting chat with agent:', agent.agent_name, agent.version);

      const chatResponse = await apiService.startChat(
        {
          agent_name: agent.agent_name,
          agent_version: agent.version
        },
        user?.user_id
      );

      console.log('✅ Chat response:', chatResponse);

      if (!chatResponse.success) {
        throw new Error(chatResponse.error || 'Failed to start chat');
      }

      const newChatId = chatResponse.data.chat_id;
      console.log('✅ Chat created:', newChatId);

      // ✅ ADD: Navigate with from=ai parameter
      navigate(`/dashboard/chat/${newChatId}?from=ai`);

    } catch (err) {
      console.error('❌ Failed to start chat:', err);
      alert(err.message || 'Failed to start chat');
    }
  };

  return (
    <AdminPageTemplate
      title="Allegory Intelligence"
      subtitle="Manage AI agents, analytics, and machine learning models"
      icon={Brain}
    >
      <div className={styles.container}>
        {/* Stats Cards */}
        <AgentStatsCards stats={stats} isLoading={isLoadingStats} />

        {/* Error Message */}
        {error && (
          <div className={styles.errorBanner}>
            <p>{error}</p>
            <button onClick={() => setError(null)} className={styles.dismissButton}>
              Dismiss
            </button>
          </div>
        )}

        {/* Agents Table */}
        <AgentTable
          agents={agents}
          isLoading={isLoadingAgents}
          onViewDetails={handleViewDetails}
          onStartChat={handleStartChat}
        />

        {/* Agent Details Modal */}
        <AgentDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          agentDetails={selectedAgent}
          isLoading={isLoadingDetails}
          onStartChat={handleStartChat}
        />
      </div>
    </AdminPageTemplate>
  );
};

export default AI;